import * as clusterApi from '@/api/cluster';
import { Alert, App, Button, Descriptions, Form, Input, Modal, Result, Select, Space, Steps, Table, Tag, Typography } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const { Paragraph, Text } = Typography;

/**
 * The wizard that connects a user's own ssh key to a remote cluster.
 *
 * The remote clusters have user databases unrelated to this platform's, and users
 * authenticate with the key in their own home directory. The backend therefore holds no
 * credential for them and cannot connect on their behalf: this flow is the only way a
 * cluster becomes usable, which is why every step reports what it found rather than a bare
 * pass/fail.
 *
 * Two decisions are worth pointing out in the UI text rather than hiding them:
 *  - a generated key has no passphrase, because the platform cannot answer an interactive
 *    prompt and storing one would put the user's passphrase in the platform's hands;
 *  - the remote account name is a required field with no default, because the two clusters
 *    do not share names and silently defaulting would guarantee a confusing first failure.
 */
const RemoteClusterDialog = ({ open, clusterId, clusters, onClose, onConnected }: {
    open: boolean;
    clusterId?: string;
    clusters: clusterApi.ClusterView[];
    onClose: () => void;
    onConnected: () => void;
}) => {
    const { t } = useTranslation('common');
    const { message } = App.useApp();

    const remoteClusters = clusters.filter(c => c.remote);
    const [step, setStep] = useState(0);
    const [target, setTarget] = useState<string>(clusterId || remoteClusters[0]?.id || '');
    const [keyPair, setKeyPair] = useState<clusterApi.KeyPair | undefined>();
    const [remoteUser, setRemoteUser] = useState('');
    const [host, setHost] = useState('');
    const [port, setPort] = useState(22);
    const [password, setPassword] = useState('');
    const [usePassword, setUsePassword] = useState(false);
    const [busy, setBusy] = useState(false);
    const [report, setReport] = useState<clusterApi.VerifyReport | undefined>();

    const cluster = remoteClusters.find(c => c.id === target);

    const reset = () => {
        setStep(0);
        setKeyPair(undefined);
        setRemoteUser('');
        setPassword('');
        setUsePassword(false);
        setReport(undefined);
        setBusy(false);
    };

    const close = () => { reset(); onClose(); };

    // Step 1: locate or create the key. The permission repair is reported rather than done
    // silently: ssh refuses a loosely-permissioned key with "Permission denied (publickey)",
    // which gives the user no clue that the cause was a file mode.
    const loadKey = async (generate: boolean) => {
        setBusy(true);
        const res = await clusterApi.ensureKey(generate);
        setBusy(false);
        if (!res?.ok || !res.data) {
            message.error(res?.message || t('remote_cluster.key_failed'));
            return;
        }
        setKeyPair(res.data);
        if (res.data.permFixed) {
            message.warning(t('remote_cluster.key_perm_fixed', { key: res.data.permFixed }));
        }
        if (res.data.generated) {
            message.success(t('remote_cluster.key_generated'));
        }
        setStep(1);
    };

    // Step 2: get the public key onto the remote cluster.
    const copyCommand = `ssh-copy-id -i ${keyPair?.publicPath || '~/.ssh/id_ed25519.pub'} ${remoteUser || '<' + t('remote_cluster.remote_user') + '>'}@${host || cluster?.id}`;

    const installWithPassword = async () => {
        if (!host) { message.error(t('remote_cluster.host_required')); return; }
        if (!remoteUser) { message.error(t('remote_cluster.remote_user_required')); return; }
        if (!keyPair?.publicKey) { message.error(t('remote_cluster.key_failed')); return; }
        setBusy(true);
        const res = await clusterApi.installKey({
            clusterId: target, remoteUser, host, port, password, publicKey: keyPair.publicKey
        });
        setBusy(false);
        // Clear the password from component state immediately; it must not outlive the call.
        setPassword('');
        if (!res?.ok) {
            message.error(res?.message || t('remote_cluster.install_failed'));
            return;
        }
        message.success(res.data?.alreadyPresent ? t('remote_cluster.install_present') : t('remote_cluster.install_ok'));
        setStep(2);
    };

    // Step 3: verify. This uploads the helper, inspects the remote environment and confirms
    // that the staging directory is visible from a compute node.
    const verify = async () => {
        if (!remoteUser) { message.error(t('remote_cluster.remote_user_required')); return; }
        setBusy(true);
        const res = await clusterApi.verifyCluster({ clusterId: target, remoteUser, isDefault: true });
        setBusy(false);
        if (!res?.ok) {
            message.error(res?.message || t('remote_cluster.verify_failed'));
            return;
        }
        setReport(res.data);
        setStep(3);
    };

    const candidates = report?.candidates || [];

    return (
        <Modal
            title={t('remote_cluster.title')}
            open={open}
            width={720}
            onCancel={close}
            footer={null}
            destroyOnClose
        >
            <Steps
                size="small"
                current={step}
                style={{ marginBottom: 24 }}
                items={[
                    { title: t('remote_cluster.step_key') },
                    { title: t('remote_cluster.step_install') },
                    { title: t('remote_cluster.step_verify') },
                    { title: t('remote_cluster.step_done') }
                ]}
            />

            {step === 0 && (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <Form layout="vertical">
                        <Form.Item label={t('job_submission.cluster')}>
                            <Select
                                value={target || undefined}
                                onChange={setTarget}
                                placeholder={t('job_submission.cluster_placeholder')}
                                options={remoteClusters.map(c => ({ label: c.displayName, value: c.id }))}
                            />
                        </Form.Item>
                    </Form>
                    <Alert type="info" showIcon message={t('remote_cluster.key_intro')} />
                    <Space>
                        <Button type="primary" loading={busy} onClick={() => loadKey(false)}>
                            {t('remote_cluster.find_key')}
                        </Button>
                        <Button loading={busy} onClick={() => loadKey(true)}>
                            {t('remote_cluster.generate_key')}
                        </Button>
                    </Space>
                    <Text type="secondary">{t('remote_cluster.no_passphrase_warning')}</Text>
                </Space>
            )}

            {step === 1 && keyPair && (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <Alert type="success" showIcon message={t('remote_cluster.key_found')} description={keyPair.privatePath} />
                    <div>
                        <Text strong>{t('remote_cluster.public_key')}</Text>
                        <Paragraph copyable={{ text: keyPair.publicKey }} style={{ marginTop: 4 }}>
                            <Text code style={{ wordBreak: 'break-all' }}>{keyPair.publicKey}</Text>
                        </Paragraph>
                    </div>
                    <Form layout="vertical">
                        <Form.Item
                            label={t('remote_cluster.remote_user')}
                            required
                            tooltip={t('remote_cluster.remote_user_hint')}
                        >
                            <Input
                                value={remoteUser}
                                onChange={e => setRemoteUser(e.target.value)}
                                placeholder={t('remote_cluster.remote_user_placeholder')}
                            />
                        </Form.Item>
                    </Form>
                    <Alert type="info" showIcon message={t('remote_cluster.manual_install')} description={<Text code copyable>{copyCommand}</Text>} />
                    <Space>
                        <Button type="primary" disabled={!remoteUser} onClick={() => setStep(2)}>
                            {t('remote_cluster.already_installed')}
                        </Button>
                        <Button onClick={() => setUsePassword(v => !v)}>{t('remote_cluster.install_with_password')}</Button>
                    </Space>
                    {usePassword && (
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Alert type="warning" showIcon message={t('remote_cluster.password_notice')} />
                            <Form layout="vertical">
                                <Form.Item label={t('remote_cluster.remote_host')} required>
                                    <Input value={host} onChange={e => setHost(e.target.value)} placeholder="remote-login.example.edu" />
                                </Form.Item>
                                <Form.Item label={t('remote_cluster.remote_port')}>
                                    <Input type="number" value={port} onChange={e => setPort(Number(e.target.value) || 22)} />
                                </Form.Item>
                                <Form.Item label={t('remote_cluster.remote_password')} required>
                                    <Input.Password
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        autoComplete="off"
                                    />
                                </Form.Item>
                            </Form>
                            <Button type="primary" loading={busy} disabled={!password || !host} onClick={installWithPassword}>
                                {t('remote_cluster.install_now')}
                            </Button>
                        </Space>
                    )}
                </Space>
            )}

            {step === 2 && (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <Alert type="info" showIcon message={t('remote_cluster.verify_intro')} />
                    <Descriptions size="small" column={1} bordered>
                        <Descriptions.Item label={t('job_submission.cluster')}>{cluster?.displayName || target}</Descriptions.Item>
                        <Descriptions.Item label={t('remote_cluster.remote_user')}>{remoteUser}</Descriptions.Item>
                        <Descriptions.Item label={t('remote_cluster.key')}>{keyPair?.privatePath}</Descriptions.Item>
                    </Descriptions>
                    <Space>
                        <Button type="primary" loading={busy} onClick={verify}>{t('remote_cluster.verify_now')}</Button>
                        <Button onClick={() => setStep(1)}>{t('remote_cluster.back')}</Button>
                    </Space>
                </Space>
            )}

            {step === 3 && report && (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    {report.ok ? (
                        <Result status="success" title={t('remote_cluster.connected')} subTitle={report.message} />
                    ) : (
                        <Result status="error" title={t('remote_cluster.verify_failed')} subTitle={report.message} />
                    )}
                    <Table
                        size="small"
                        pagination={false}
                        rowKey="name"
                        dataSource={report.steps}
                        columns={[
                            { title: t('remote_cluster.check'), dataIndex: 'name' },
                            {
                                title: t('job_list.state'),
                                dataIndex: 'ok',
                                width: 110,
                                render: (_: boolean, row: clusterApi.VerifyStep) => row.skipped
                                    ? <Tag>{t('remote_cluster.skipped')}</Tag>
                                    : row.ok ? <Tag color="green">{t('remote_cluster.passed')}</Tag> : <Tag color="red">{t('remote_cluster.failed')}</Tag>
                            },
                            { title: t('remote_cluster.detail'), dataIndex: 'detail' }
                        ]}
                    />
                    {candidates.length > 0 && (
                        <>
                            <Text strong>{t('remote_cluster.staging_candidates')}</Text>
                            <Table
                                size="small"
                                pagination={false}
                                rowKey="path"
                                dataSource={candidates}
                                rowClassName={row => row.path === report.chosenStaging ? 'ant-table-row-selected' : ''}
                                columns={[
                                    { title: t('remote_cluster.path'), dataIndex: 'path' },
                                    { title: t('remote_cluster.free_mb'), dataIndex: 'freeMb', width: 110 },
                                    { title: t('remote_cluster.fs'), dataIndex: 'fsType', width: 110 },
                                    {
                                        // Visibility from a compute node is what decides
                                        // whether jobs staged here can see their inputs at
                                        // all, so it gets its own column rather than being
                                        // folded into "writable".
                                        title: t('remote_cluster.compute_visible'),
                                        dataIndex: 'shared',
                                        width: 150,
                                        render: (v: boolean) => v ? <Tag color="green">{t('remote_cluster.yes')}</Tag> : <Tag color="orange">{t('remote_cluster.unverified')}</Tag>
                                    }
                                ]}
                            />
                        </>
                    )}
                    <Space>
                        {report.ok && <Button type="primary" onClick={() => { close(); onConnected(); }}>{t('remote_cluster.done')}</Button>}
                        {!report.ok && <Button type="primary" onClick={() => setStep(2)}>{t('remote_cluster.retry')}</Button>}
                        <Button onClick={close}>{t('remote_cluster.close')}</Button>
                    </Space>
                </Space>
            )}
        </Modal>
    );
};

export default RemoteClusterDialog;
